<template>
  <div
    class="product-filter product-filter-list"
    :class="[isMobileLayout ? 'ml-16' : '']"
  >
    <el-collapse :accordion="false" :value="'productTypes'">
      <el-collapse-item name="productTypes">
        <template slot="title">
          <div class="subtitle1">
            {{ typeOfProductLabel }}
          </div>
        </template>
        
        <el-tree
          ref="productTypeTree"
          class="mb-10"
          node-key="id"
          :empty-text="'empty.text'"
          :data="productTypeTreeData"
          :props="productTypeTreeProps"
          :class="'tree_layout'"
          default-expand-all
          :default-checked-keys="productTypeTreeDefaultCheckedKeys"
          highlight-current
          show-checkbox
          @check-change="handleCheckChange"
        />
      </el-collapse-item>

      <el-collapse-item name="productPrices">
        <template slot="title">
          <div class="subtitle1">
            {{ priceLabel }}
          </div>
        </template>

        <el-form
          ref="priceForm"
          :model="priceForm"
          :rules="rules"
          class="priceForm"
        >
          <div id="price" class="flex">
            <el-form-item prop="min">
              <el-input v-model.number="priceForm.min" placeholder="Min" />
            </el-form-item>
            <el-form-item prop="max">
              <el-input v-model.number="priceForm.max" placeholder="Max" />
            </el-form-item>
          </div>
          <div class="d-flex text-right mr-16">
            <el-button type="primary d-flex align-items-center" @click="resetForm('priceForm')">
              Reset
            </el-button>
            <el-button type="primary d-flex align-items-center mr-16" @click="submitForm('priceForm')">
              {{ $t("products.Apply") }}
            </el-button>
          </div>
        </el-form>
      </el-collapse-item>

      <el-collapse-item name="productParticularity" v-show="!isShopPage">
        <template slot="title">
          <div class="subtitle1">
            {{ particularityLabel }}
          </div>
        </template>
        <el-tree
          ref="productParticularityTree"
          class="mb-10"
          node-key="id"
          :empty-text="'empty.text'"
          :data="productParticularityTreeData"
          :props="productParticularityTreeProps"
          :class="'tree_layout'"
          default-expand-all
          :default-checked-keys="productParticularityTreeDefaultCheckedKeys"
          highlight-current
          show-checkbox
          @check-change="handleCheckChange"
        />
      </el-collapse-item>

      <el-collapse-item name="productAvailability" v-show="!isShopPage">
        <template slot="title">
          <div class="subtitle1">
            {{ availabilityLabel }}
          </div>
        </template>
        <el-tree
          ref="productAvailibilityTree"
          class="mb-10"
          node-key="id"
          :empty-text="'empty.text'"
          :data="productAvailabilityTreeData()"
          :props="productAvailabilityTreeProps"
          :class="'tree_layout'"
          :default-checked-keys="availabilityTreeDefaultCheckedKeys"
          default-expand-all
          highlight-current
          show-checkbox
          @check-change="handleCheckChange"
        />
      </el-collapse-item>

      <el-collapse-item name="country" class="product-availibility-tree" v-show="!isShopPage">
        <template slot="title">
          <div class="subtitle1">
            {{ countryRegionLabel }}
          </div>
        </template>

        <Scrollable
          id="tree-root"
          class="tree__root product_filter ml"
          :horizontal="false"
        >
          <el-tree
            ref="countryRegionTree"
            node-key="id"
            class="mb-10"
            :empty-text="'empty.text'"
            :data="regionTreeData"
            :props="regionTreeProps"
            :class="'tree_layout'"
            :default-checked-keys="countryRegionTreeDefaultCheckedKeys"
            highlight-current
            show-checkbox
            @check-change="handleCheckChange"
          />
        </Scrollable>

        <el-divider />
      </el-collapse-item>

      <el-collapse-item name="productStatus">
        <template slot="title">
          <div class="subtitle1">
            {{ productStatusLabel }}
          </div>
        </template>
        <el-tree
          ref="productAvailabilityStatusTree"
          class="mb-10"
          node-key="id"
          :empty-text="'empty.text'"
          :data="$i18n.t('products.productAvailabilityStatusTree')"
          :props="productAvailabilityStatusTreeProps"
          :class="'tree_layout'"
          :default-checked-keys="availabilityStatusTreeDefaultCheckedKeys"
          default-expand-all
          highlight-current
          show-checkbox
          @check-change="handleCheckChange"
        />
      </el-collapse-item>

    </el-collapse>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, find, forEach, get, isEmpty } from "@/utils/lodash";

import layoutUtils from "@/utils/layout-utils";
import { wrapInArray } from "@/utils/common-utils";
import {
  getDefaultAvailability,
  isBoutiqueInventory,
  parseAvailabilityStatusQuery,
} from "@/utils/market-utils";
import Scrollable from "@/components/scrollable.vue";

const AVAILABILITY_STATUS_KEY_MAP = { AVAILABLE: 1, UPCOMING: 2 };

// Codes the single "Boires" entrepôt node covers, on both markets. Mirrors the
// `boires` -> Brouhh/MERCH_MTL/BERGEVILLE expansion done in emitFilterChange().
const BOIRES_WAREHOUSE_CODES = [
  "boires",
  "Brouhh",
  "MERCH_MTL",
  "BERGEVILLE",
  "MERCH_TORONTO",
  "STORE_TORONTO",
];

function getAvailabilityStatusKeys(model) {
  if (!model || model.length === 0) {
    return [1, 2];
  }
  const keys = [];
  forEach(model, (code) => {
    const id = AVAILABILITY_STATUS_KEY_MAP[code];
    if (id != null && keys.indexOf(id) === -1) {
      keys.push(id);
    }
  });
  return keys.length > 0 ? keys : [1, 2];
}

export default {
  components: {
    Scrollable,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({
        productTypes: [],
        attributes: [],
        availability: [],
        countries: [],
        regions: [],
        availabilityStatus: [],
      }),
    },
    productTypesModel: {
      type: Array,
      default: () => [],
    },
    attributesModel: {
      type: Array,
      default: () => [],
    },
    availabilityModel: {
      type: Array,
      default: () => ["IP", "SAQ", "MERCH_MTL", "Brouhh", "BERGEVILLE"],
    },
    availabilityStatusModel: {
      type: Array,
      default: () => [
        "AVAILABLE",
        "UPCOMING"
      ],
    },
    countriesModel: {
      type: Array,
      default: () => [],
    },
    regionsModel: {
      type: Array,
      default: () => [],
    },
    minPriceModel: {
      type: Number,
      default: null,
    },
    maxPriceModel: {
      type: Number,
      default: null,
    },
    filterReset: {
      type: Boolean,
      default: false,
    },
    productInventory: {
      type: String,
      default: null,
    },
  },
  data() {
    /* eslint consistent-return: ["error", { "treatUndefinedAsUnspecified": true }] */
    const checkPrice = (rule, value, callback) => {
      if (!value && value !== 0) {
        return callback(
          new Error(this.$i18n.t("products.filters.validation.priceNovalue"))
        );
      }
      if (!Number.isInteger(value)) {
        return callback(
          new Error(this.$i18n.t("products.filters.validation.priceNotANumber"))
        );
      }
      if (value < 0) {
        return callback(
          new Error(
            this.$i18n.t("products.filters.validation.priceLessThanZero")
          )
        );
      }

      return callback();
    };
    return {
      clonedProductTypesModel: this.productTypesModel,
      clonedAttributesModel: this.attributesModel,
      clonedAvailabilityModel: this.availabilityModel,
      clonedAvailabilitySatatusModel: this.availabilityStatusModel,
      clonedCountriesModel: this.countriesModel,
      clonedRegionsModel: this.regionsModel,
      modelName: {
        productType: "productType",
        attribute: "attribute",
        availability: "availability",
        country: "country",
        region: "region",
        availabilityStatus: "availabilityStatus",
      },
      regionTreeProps: {
        label: "name",
        children: "regions",
        disabled: (data) => {
          const regions = get(data, "regions");
          return regions && regions.length === 0;
        },
      },
      productTypeTreeDefaultCheckedKeys: [],
      productParticularityTreeDefaultCheckedKeys: [],
      availabilityTreeDefaultCheckedKeys: [2,3,4],
      availabilityStatusTreeDefaultCheckedKeys: getAvailabilityStatusKeys(
        this.availabilityStatusModel
      ),
      productTypeTreeProps: {
        label: "name",
      },
      productParticularityTreeProps: {
        label: "value",
      },
      productAvailabilityTreeProps: {
        label: "name",
      },
      productAvailabilityStatusTreeProps: {
        label: "name",
      },
      priceForm: {
        min: this.minPriceModel,
        max: this.maxPriceModel,
      },
      countryRegionTreeDefaultCheckedKeys: [],
      rules: {
        min: [
          {
            validator: checkPrice,
            trigger: "blur",
          },
        ],
        max: [
          {
            validator: checkPrice,
            trigger: "blur",
          },
        ],
      },
      countryCode: localStorage.getItem("countryCode"),
    };
  },
  computed: {
    ...mapGetters('filter', ['getSelectedFilters']),
    isShopPage() {
      // Boutique mode hides the Couleur/Entrepôts/Origine sections and trims
      // the Type list. It must key off a STORE_* inventory only: the agency
      // products page writes its warehouse selection into the same `inventory`
      // param, and treating that as "shop" made every other filter section
      // vanish as soon as one filter was applied — leaving no way to uncheck it
      // short of a full reset.
      return isBoutiqueInventory(get(this.$route, "query.inventory"));
    },
    typeOfProductLabel() {
      return this.$i18n.t("products.filters.label.headers.typeOfProduct");
    },
    priceLabel() {
      return this.$i18n.t("products.filters.label.headers.price");
    },
    particularityLabel() {
      return this.$i18n.t("products.filters.label.headers.particularities");
    },
    availabilityLabel() {
      return this.$i18n.t("products.filters.label.headers.availability");
    },
    productStatusLabel() {
      return this.$i18n.t("products.filters.label.headers.product_status");
    },
    countryRegionLabel() {
      return this.$i18n.t("products.filters.label.headers.countryRegion");
    },
    regionTreeData() {
  const rootElement = {
    id: 0,
    name: this.$i18n.t("products.filters.label.all"),
    category: this.modelName.region,
  };

  if (
    this.filters &&
    this.filters.countries &&
    this.filters.countries.length !== 0
  ) {
    const clonedCountries = cloneDeep(this.filters.countries);

    // Helper: safely compare name properties (null/undefined go last)
    const safeCompareName = (a, b) => {
      const na = a && a.name;
      const nb = b && b.name;

      if (na == null && nb == null) return 0;
      if (na == null) return 1; // put null/undefined after defined names
      if (nb == null) return -1;
      // Ensure they are strings before calling localeCompare
      return String(na).localeCompare(String(nb));
    };

    // Sort countries in ascending order by name
    clonedCountries.sort(safeCompareName);

    forEach(clonedCountries, (country) => {
      // Sort regions in ascending order by name
      if (!isEmpty(country.regions)) {
        country.regions.sort(safeCompareName);

        // Sort cities within each region in ascending order
        forEach(country.regions, (region) => {
          if (!isEmpty(region.cities)) {
            region.cities.sort(safeCompareName);
          }

          region.originalId = region.id;
          region.category = this.modelName.region;
          region.parent = country;
        });
      }

      country.originalId = country.id;
      country.id = country.code;
      country.category = this.modelName.country;
      country.parent = rootElement;
    });

    return [rootElement, ...clonedCountries];
  }

  return [rootElement];
},

    productTypeTreeData() {
      const rootElement = {
        id: 0,
        name: this.$i18n.t("products.filters.label.all"),
        category: this.modelName.productType,
      };
      
      if (this.filters && this.filters.productTypes) {
        // Helper function to sort product types and their specifics
        const sortProductTypes = (products) => {
          return products
            .map(product => ({
              ...product,
              // Sort specifics array if it exists
              specifics: product.specifics ? 
                [...product.specifics].sort((a, b) => a.name.localeCompare(b.name)) : 
                []
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        };
        
        const filterShopProduct = this.filters.productTypes.filter(
          (productType) => !['Menu', 'Merchandise', 'Grocery', 'Marchandise'].includes(productType.name)
        );
        
        const sortedShopProducts = sortProductTypes(filterShopProduct);
        const shopProduct = [rootElement, ...sortedShopProducts];
        
        const filteredProduct = this.filters.productTypes.filter(
          (productType) => !["Menu", "Wine", "Vin", "Beer", "Bière", "Cider", "Cidre", "Aperitif", "Apéritif", "Spirits", "Spiritueux", "Merchandise", "Marchandise"].includes(productType.name)
        );
        
        const sortedRestaurantProducts = sortProductTypes(filteredProduct);
        const restaurantProduct = [rootElement, ...sortedRestaurantProducts];
        
        return !this.isShopPage ? shopProduct : restaurantProduct;
      }
      
      return [rootElement];
    },
    // productTypeTreeData() {
    //   const rootElement = {
    //     id: 0,
    //     name: this.$i18n.t("products.filters.label.all"),
    //     category: this.modelName.productType,
    //   };
    //     if (this.filters && this.filters.productTypes) {
    //     return [rootElement, ...this.filters.productTypes];
    //   }
    //   return [rootElement];
    // },
    
    productParticularityTreeData() {
      const rootElement = {
        id: 0,
        value: this.$i18n.t("products.filters.label.all"),
        category: this.modelName.attribute,
      };

      if (this.filters && this.filters.attributes) {
        return [rootElement, ...this.filters.attributes];
      }

      return [rootElement];
    },
    isFilterReset() {
      return this.filterReset;
    },
    isMobileLayout() {
      return layoutUtils.isMobileLayout(this.$mq);
    },
    isProductTypesReady() {
      return !isEmpty(this.filters.productTypes);
    },
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        // beforeRouteUpdate doesn't fire on this component (it's a child of
        // products.vue, not the matched route component), so this watcher is
        // the only place that catches in-place URL updates. Re-apply the
        // URL-priority logic so footer/CMS filtered links work even when the
        // same instance persists across navigations.
        this.applySavedFilters(this.getSelectedFilters, newQuery);
      },
      immediate: true,
    },
    clonedAttributesModel(newVal, oldVal) {
      if (newVal.length != oldVal.length) {
        this.triggerFilterChange();
      }
    },
    clonedAvailabilityModel(newVal, oldVal) {
      if (newVal.length != oldVal.length) {
        this.triggerFilterChange();
      }
    },
    clonedProductTypesModel(newVal, oldVal) {
      if (newVal.length != oldVal.length) {
        this.triggerFilterChange();
      }
    },
    clonedCountriesModel(newVal, oldVal) {
      if (newVal.length != oldVal.length) {
        this.triggerFilterChange();
      }
    },
    clonedRegionsModel(newVal, oldVal) {
      if (newVal.length != oldVal.length) {
        this.triggerFilterChange();
      }
    },
    clonedAvailabilitySatatusModel(newVal, oldVal) {
      if (newVal.length != oldVal.length) {
        this.triggerFilterChange();
      }
    },
    isFilterReset() {
      if (this.filterReset) {
        this.$refs.productTypeTree.setCheckedKeys([]);
        this.$refs.countryRegionTree.setCheckedKeys([]);
        this.$emit("filter-reset");
      }
    },
    isProductTypesReady() {
      this.initTreeDefaultCheckedKeys();
    },
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.applySavedFilters(vm.getSelectedFilters, to.query);
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.applySavedFilters(this.getSelectedFilters, to.query);
    next();
  },
  mounted() {
    this.initTreeDefaultCheckedKeys();
    this.applySavedFilters(this.getSelectedFilters);
    this.$_onCountry = (countryCode) => {
      this.countryCode = countryCode;
      this.productAvailabilityTreeData();
      // The Entrepôts tree is rebuilt from the new market's nodes, but the
      // selection behind it still held the previous market's codes — so the
      // next filter change re-emitted Quebec warehouses on the Ontario site and
      // the checked state matched nothing in the new tree. Reset the selection
      // to the new market and refresh the tree's checked keys with it.
      if (!isBoutiqueInventory(this.productInventory)) {
        this.clonedAvailabilityModel = getDefaultAvailability(countryCode);
        this.initTreeDefaultCheckedKeys();
      }
    };
    this.$root.$on("chancountryCode", this.$_onCountry);
    // this.setFiltersToQuery();
  },
  beforeDestroy() {
    this.$root.$off("chancountryCode", this.$_onCountry);
  },
  methods: {
    ...mapActions('filter', ['setSelectedFilters']),
    triggerFilterChange() {
      // this.setFiltersToQuery();
      this.emitFilterChange();
      this.setSelectedFilters(this.getCurrentFilters());
    },
    getCurrentFilters() {
      return {
        productTypes: this.clonedProductTypesModel,
        attributes: this.clonedAttributesModel,
        availability: this.clonedAvailabilityModel,
        countries: this.clonedCountriesModel,
        regions: this.clonedRegionsModel,
        availabilityStatus: this.clonedAvailabilitySatatusModel,
        minPrice: this.priceForm.min,
        maxPrice: this.priceForm.max,
      };
    },
    applySavedFilters(filters, query) {
      // When the URL carries a filter param (footer/CMS links, shared URLs),
      // honour it directly so saved Vuex filters don't clobber the incoming URL.
      // `query` is optional so beforeRouteUpdate can pass `to.query` — by the
      // time that guard fires, `this.$route` still points at the old route.
      const q = query || this.$route.query;

      // Snapshot saved values up-front. Each cloned-model assignment below
      // triggers a watcher → triggerFilterChange → setSelectedFilters that
      // overwrites the Vuex `filters` we're reading from, so without the
      // snapshot only the first dimension would survive.
      const snap = {
        productTypes: (filters.productTypes || this.productTypesModel || []).slice(),
        attributes: (filters.attributes || this.attributesModel || []).slice(),
        availability: (filters.availability || this.availabilityModel || []).slice(),
        countries: (filters.countries || this.countriesModel || []).slice(),
        regions: (filters.regions || this.regionsModel || []).slice(),
        availabilityStatus: (filters.availabilityStatus || this.availabilityStatusModel || []).slice(),
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
      };
      const urlOr = (key, savedValue) =>
        q[key] != null ? wrapInArray(q[key]) : savedValue;
      const urlNumberOr = (key, savedValue) =>
        q[key] != null && q[key] !== "" ? Number(q[key]) : savedValue;

      this.clonedProductTypesModel = urlOr("type", snap.productTypes);
      this.clonedAttributesModel = urlOr("a", snap.attributes);
      // Entrepôt codes are market-specific, and the saved Vuex filters outlive a
      // market switch — so falling back to them re-seeded Ontario with Quebec's
      // warehouses. Those tick the "Boires" node through BOIRES_WAREHOUSE_CODES,
      // which is why a reset on Ontario came back showing "Boires" instead of
      // "Tout". No `inventory` in the URL means no selection; emitFilterChange()
      // supplies the current market's default at query time.
      this.clonedAvailabilityModel =
        q.inventory != null ? wrapInArray(q.inventory) : [];
      this.clonedCountriesModel = urlOr("c", snap.countries);
      this.clonedRegionsModel = urlOr("r", snap.regions);
      this.clonedAvailabilitySatatusModel = q.show != null
        ? parseAvailabilityStatusQuery(q.show)
        : snap.availabilityStatus;
      this.priceForm.min = urlNumberOr("min", snap.minPrice);
      this.priceForm.max = urlNumberOr("max", snap.maxPrice);
      this.initTreeDefaultCheckedKeys();
    },
    setFiltersToQuery() {
      const initQuery = {
        ...this.$route.query,
        type: this.clonedProductTypesModel,
        inventory: this.productInventory,
        a: this.clonedAttributesModel,
        c: this.clonedCountriesModel,
        r: this.clonedRegionsModel,
        min: this.priceForm.min,
        max: this.priceForm.max,
      };

      const query = {};

      forEach(initQuery, (value, key) => {
        if (value != null) {
          query[key] = value;
        }
      });

      this.$router
        .push({
          query,
        })
        .catch(() => {});
    },
    emitFilterChange() {
      const getCountryCode = localStorage.getItem('countryCode');
      // Only collapse availability to a single inventory on the boutique/shop
      // pages (STORE_MTL / STORE_QC / STORE_TORONTO). On the agency products
      // page the `inventory` URL param carries the FULL warehouse set
      // (IP, SAQ, Brouhh, ...), so `this.productInventory` (= that param's
      // first code, e.g. "IP") must NOT collapse availability to just that one
      // code — doing so dropped every other warehouse's products and could
      // empty the list (e.g. multi-country selections showing nothing).
      var availability = isBoutiqueInventory(this.productInventory)
        ? [this.productInventory]
        : this.clonedAvailabilityModel;
      // "Tout" in the Entrepôts tree leaves clonedAvailabilityModel empty, and
      // that means "every warehouse of THIS market" — never "no inventory
      // restriction". An empty array is dropped from the query string, and the
      // CMS search endpoints answer an absent `inventory` with variants from
      // every company's inventory: the same wine came back twice, at the QC and
      // the ON price, and could be added to the cart from the wrong company.
      //
      // The fallback used to be suppressed whenever `inventory` was already in
      // the URL, which is the case from the second filter change onwards (the
      // first one writes it there). So ticking a country/region after any other
      // filter silently un-scoped the whole listing.
      if (!isBoutiqueInventory(this.productInventory) && availability.length === 0) {
        availability = getDefaultAvailability(getCountryCode);
      }
      if(this.clonedAvailabilityModel.includes('boires')) {
          availability = availability.filter(option => option !== "boires");
          availability.push("Brouhh", "MERCH_MTL", "BERGEVILLE");
      }
      this.$emit("filter-change", {
        productTypes: this.clonedProductTypesModel,
        availability: availability,
        attributes: this.clonedAttributesModel,
        optionTotals: this.getOptionTotals(),
        countries: this.clonedCountriesModel,
        regions: this.clonedRegionsModel,
        minPrice: this.priceForm.min,
        maxPrice: this.priceForm.max,
        availabilityStatus:this.clonedAvailabilitySatatusModel,
        req: true,
        filterEmit: true,
      });
    },
    updateFilters() {
      const productTypesModel = [];
      const attributesModel = [];
      const regionsModel = [];
      const availabilityModel = [];
      const availabilityStatusModel = [];

      this.updateFilterElements(this.$refs.productTypeTree, productTypesModel);

      this.updateFilterElements(
        this.$refs.productParticularityTree,
        attributesModel
      );

      this.updateFilterElements(
        this.$refs.productAvailibilityTree,
        availabilityModel
      );
      this.updateFilterElements(
        this.$refs.productAvailabilityStatusTree,
        availabilityStatusModel
      );

      if (this.$refs.countryRegionTree.getCheckedNodes().length > 1) {
        this.$refs.countryRegionTree.setChecked(0, false);
      } else if (this.$refs.countryRegionTree.getCheckedNodes().length === 0) {
        this.$refs.countryRegionTree.setChecked(0, true);
      }

      forEach(this.$refs.countryRegionTree.getCheckedNodes(), (node) => {
        if (
          node &&
          node.id !== 0 &&
          node.category === this.modelName.region &&
          regionsModel.findIndex((code) => code === node.id) === -1
        ) {
          regionsModel.push(node.id);
        }
      });

      this.clonedProductTypesModel = productTypesModel;
      this.clonedAttributesModel = attributesModel;
      this.clonedRegionsModel = regionsModel;
      this.clonedAvailabilityModel = availabilityModel;
      this.clonedAvailabilitySatatusModel = availabilityStatusModel;
    },
    updateFilterElements(tree, model) {
      if (tree.getCheckedNodes().length > 1) {
        tree.setChecked(0, false);
      } else if (tree.getCheckedNodes().length === 0) {
        tree.setChecked(0, true);
      }

      forEach(tree.getCheckedNodes(), (node) => {
        if (
          node &&
          node.id !== 0 &&
          model.findIndex((code) => code === node.code) === -1
        ) {
          model.push(node.code);
        }
      });
    },
    treeForCategory(category) {
      switch (category) {
        case this.modelName.productType:
          return this.$refs.productTypeTree;
        case this.modelName.attribute:
          return this.$refs.productParticularityTree;
        case this.modelName.region:
          return this.$refs.countryRegionTree;
        case this.modelName.availability:
          return this.$refs.productAvailibilityTree;
        case this.modelName.availabilityStatus:
          return this.$refs.productAvailabilityStatusTree;
        default:
          return null;
      }
    },
    // Every key in a tree except the "Tout" node (id 0).
    allOptionKeys(tree) {
      const nodesMap = tree && tree.store ? tree.store.nodesMap : null;
      if (!nodesMap) {
        return [];
      }

      return Object.keys(nodesMap)
        .map((key) => nodesMap[key])
        .filter((node) => node && node.data && node.data.id !== 0)
        .map((node) => node.key);
    },
    // Unticking "Tout" ticks every option instead of snapping back to "Tout",
    // which is what makes "all but one or two" possible: untick Tout, then
    // untick the one or two you don't want. Ticking "Tout" still clears the
    // rest, so the section reads the same as before at rest.
    //
    // Origine is deliberately excluded: it is a country/region hierarchy rather
    // than a flat list, so "all" there means expanding both levels and is its
    // own change.
    supportsSelectAll(category) {
      return (
        category === this.modelName.productType ||
        category === this.modelName.attribute ||
        category === this.modelName.availability ||
        category === this.modelName.region
      );
    },
    // Origine is a country -> region tree, but only region nodes are emitted
    // (see updateFilters), so its total is the region count, not every node.
    // Ticking a country ticks its regions through el-tree, which is what makes
    // "all but France" work by unticking one country.
    regionOptionCount() {
      const tree = this.$refs.countryRegionTree;
      const nodesMap = tree && tree.store ? tree.store.nodesMap : null;
      if (!nodesMap) {
        return 0;
      }

      return Object.keys(nodesMap).filter((key) => {
        const node = nodesMap[key];
        return (
          node &&
          node.data &&
          node.data.id !== 0 &&
          node.data.category === this.modelName.region
        );
      }).length;
    },
    // How many options each flat section offers, so the parent can tell "all of
    // them" from "some of them" when counting active filters. It has to come
    // from the tree: the store lists 9 product-type codes (MARCHANDISE, MENU…)
    // while the tree only offers 7, so a store-based comparison never matches.
    //
    // Collapsing a complete selection to [] instead was tried and does not
    // work: the sidebar re-seeds its trees from the URL, so an empty model
    // immediately snapped the section back to "Tout" and the feature appeared
    // not to work at all.
    getOptionTotals() {
      return {
        productTypes: this.allOptionKeys(this.$refs.productTypeTree).length,
        attributes: this.allOptionKeys(this.$refs.productParticularityTree)
          .length,
        regions: this.regionOptionCount(),
      };
    },
    selectAllOptions(tree) {
      const keys = this.allOptionKeys(tree);
      if (isEmpty(keys)) {
        // Nothing to select — fall back to the old behaviour rather than
        // leaving the section with no checkbox ticked at all.
        this.resetTreeCheckboxes(tree);
        return;
      }

      tree.setCheckedKeys(keys);
      this.updateFilters();
    },
    handleCheckChange(data, checked) {
      const allLabel = this.$i18n.t("products.filters.label.all");
      if (data.name === allLabel || data.value === allLabel) {
        const tree = this.treeForCategory(data.category);
        if (tree == null) {
          return;
        }

        if (checked === false && this.supportsSelectAll(data.category)) {
          this.selectAllOptions(tree);
          return;
        }

        this.resetTreeCheckboxes(tree);
      } else {
        this.updateFilters();
      }
    },
    resetTreeCheckboxes(tree) {
      const checkedKeys = tree.getCheckedKeys();
      if (
        (isEmpty(checkedKeys) && tree.data != null) ||
        find(checkedKeys, (key) => key === 0) != null
      ) {
        tree.setCheckedKeys([0]);
      }
    },
    initTreeDefaultCheckedKeys() {
      if (this.$route.query.type && this.$route.query.type.length > 0) {
        this.clonedProductTypesModel = wrapInArray(this.$route.query.type);
      }

      this.productTypeTreeDefaultCheckedKeys =
        this.getDefaultSelectedIdentifiers(
          this.clonedProductTypesModel,
          this.filters.productTypes
        );
      this.availabilityTreeDefaultCheckedKeys = this.getAvailabilityCheckedKeys(
        this.clonedAvailabilityModel
      );
      this.availabilityStatusTreeDefaultCheckedKeys = getAvailabilityStatusKeys(
        this.clonedAvailabilitySatatusModel
      );
      this.productParticularityTreeDefaultCheckedKeys =
        this.getDefaultSelectedIdentifiers(
          this.clonedAttributesModel,
          this.filters.attributes
        );

      const defaultSelectedIdentifiers = [];
      if (isEmpty(this.clonedRegionsModel)) {
        defaultSelectedIdentifiers.push(0);
      } else {
        forEach(this.clonedRegionsModel, (regionId) => {
          defaultSelectedIdentifiers.push(regionId);
        });
      }
      this.countryRegionTreeDefaultCheckedKeys = defaultSelectedIdentifiers;
    },
    // The Entrepôts tree is rendered from the i18n data, not from
    // `filters.availability` — the product store's setFilters mutation never
    // populates that key — so its checked state has to be derived from the same
    // nodes the tree is built from. Matching against the (always undefined)
    // store key left every warehouse unchecked whenever `inventory` was in the
    // URL; that went unnoticed while this section was hidden on those pages.
    getAvailabilityCheckedKeys(model) {
      const nodes = this.productAvailabilityTreeData().filter(
        (node) => node.id !== 0
      );

      if (isEmpty(model) || isEmpty(nodes)) {
        return [0];
      }

      const keys = [];
      forEach(nodes, (node) => {
        // The "Boires" node stands for the group of own-warehouse codes that
        // emitFilterChange() expands `boires` into, so any one of them ticks it.
        const codes =
          BOIRES_WAREHOUSE_CODES.indexOf(node.code) > -1
            ? BOIRES_WAREHOUSE_CODES
            : [node.code];
        if (codes.some((code) => model.indexOf(code) > -1)) {
          keys.push(node.id);
        }
      });

      // Nothing matched, or everything did: both mean "Tout", which is how the
      // page renders before any warehouse selection reaches the URL.
      return keys.length === 0 || keys.length === nodes.length ? [0] : keys;
    },
    getDefaultSelectedIdentifiers(model, filters) {
      const getType = this.$router.currentRoute?.query?.type;
      const defaultSelectedIdentifiers = [];
      if (isEmpty(model)) {
        defaultSelectedIdentifiers.push(0);
      } else if (getType == 'MENU') {
        forEach(model, () => {
          const foundElement = find(
            filters,
            (filterElement) => filterElement.code === getType
          );
          if (foundElement != null) {
            defaultSelectedIdentifiers.push(foundElement.id);
          }
        });
      }
       else {
        forEach(model, (code) => {
          const foundElement = find(
            filters,
            (filterElement) => filterElement.code === code
          );
          if (foundElement != null) {
            defaultSelectedIdentifiers.push(foundElement.id);
          }
        });
      }

      return defaultSelectedIdentifiers;
    },
    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.triggerFilterChange();
        } else {
          return false;
        }

        return true;
      });
    },
    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.priceForm.min = null;
      this.priceForm.max = null;
      this.triggerFilterChange();
    },
    productAvailabilityTreeData() {
      const rootElement = {
        id: 0,
        name: this.$i18n.t("products.filters.label.all"),
        category: this.modelName.availability,
      };
      if (this.countryCode == "ON") {
        return [rootElement,...this.$i18n.t("products.productAvailabilityTreeDataOn")];
      }
      return [rootElement, ...this.$i18n.t("products.productAvailabilityTreeDataQc")];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.el-tree {
  .el-tree-node__label {
    color: #272421 !important;
  }
  & span {
    font-family: $font-family-bold-book;
    font-weight: $font-weight-bold-book;

    @media screen and (min-width: $media-sm) {
      font-size: 13px;
      letter-spacing: 0.25px;
    }

    @media screen and (max-width: $media-sm) {
      font-size: 13px;
      letter-spacing: 0.25px;
    }
  }
}
.el-checkbox__input.is-checked {
  & .el-checkbox__inner {
    background-color: $color-primary !important;
    border-color: $color-primary !important;
  }
}
.el-checkbox__input.is-checked .el-checkbox__inner,
.el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: $color-primary !important;
  border-color: $color-primary !important;
}
.el-drawer__header {
  text-align: left;
  margin-bottom: 12px !important;
  padding-left: 16px !important;
  font-family: $font-family-bold;
  font-weight: $font-weight-bold;
  color: #333333 !important;
  font-size: 18px !important;
}
.product_filter {
  height: 100px !important;
}
.tree_layout {
  margin-left: 0 !important;
  width: 150%;
}
.el-icon-caret-right {
  position: absolute;
  left: 165px;
}
#price {
  & div {
    & input {
      width: 80px;
      height: 25px;
      margin-right: 15px;
      font-family: "Futura ", "Futura", sans-serif;
      font-size: 12px;
      text-align: center !important;
      color: #2c3e50;
      line-height: 1 !important;
    }
  }
}

.product-availibility-tree {
  .tree_layout {
    .el-checkbox__original {
      width: 10px !important;
    }
  }
}
.el-collapse-item__header .subtitle1 {
  color: #272421;
}
</style>
